import { useParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { getRedirectByCode } from '@/mocks/radioRedirects';

export default function RedirectPage() {
  const { code } = useParams<{ code: string }>();
  const [status, setStatus] = useState<'loading' | 'redirecting' | 'not-found'>('loading');

  useEffect(() => {
    if (!code) {
      setStatus('not-found');
      return;
    }

    const redirect = getRedirectByCode(code);

    if (!redirect) {
      setStatus('not-found');
      return;
    }

    setStatus('redirecting');

    let targetUrl: string;

    if (redirect.customDomain) {
      const domain = redirect.customDomain.startsWith('http')
        ? redirect.customDomain
        : `https://${redirect.customDomain}`;
      targetUrl = domain;
    } else {
      targetUrl = `/radio/${redirect.frequency}`;
    }

    const timer = setTimeout(() => {
      window.location.href = targetUrl;
    }, 1500);

    return () => clearTimeout(timer);
  }, [code]);

  const redirect = code ? getRedirectByCode(code) : undefined;

  if (!redirect) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center p-4">
        <div className="text-center max-w-sm">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-foreground-100 flex items-center justify-center">
            <i className="ri-link-unlink text-3xl text-foreground-400"></i>
          </div>
          <h1 className="text-xl font-bold text-foreground-950 mb-2">Enlace no valido</h1>
          <p className="text-foreground-500 text-sm mb-6">
            Este codigo QR apunta a un recurso que ya no existe o el codigo es incorrecto.
          </p>
          <a
            href="/"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 font-semibold rounded-xl transition-all cursor-pointer text-sm whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            Ir al inicio
          </a>
        </div>
      </div>
    );
  }

  const targetLabel = redirect.customDomain
    ? redirect.customDomain
    : `RadioFrecuenciaWeb`;

  return (
    <div className="min-h-screen bg-background-50 flex items-center justify-center p-4">
      <div className="text-center max-w-sm w-full">
        {/* Logo */}
        <div className="w-24 h-24 mx-auto mb-6 rounded-2xl bg-background-100 overflow-hidden shadow-sm">
          <img
            src={redirect.logoUrl}
            alt={redirect.radioName}
            className="w-full h-full object-cover"
          />
        </div>

        {/* Radio name */}
        <h1 className="text-lg font-bold text-foreground-950 mb-1">{redirect.radioName}</h1>
        <p className="text-foreground-400 text-sm mb-6">
          {redirect.frequency} MHz
        </p>

        {/* Redirect info */}
        <div className="bg-background-100 rounded-xl p-4 mb-6">
          <p className="text-foreground-500 text-xs mb-3">
            {redirect.customDomain
              ? `Redirigiendo a su dominio propio...`
              : `Redirigiendo a la pagina de la radio en RadioFrecuenciaWeb...`}
          </p>

          {/* Spinner */}
          <div className="flex items-center justify-center gap-3">
            <div className="w-5 h-5 border-2 border-primary-500/30 border-t-primary-500 rounded-full animate-spin"></div>
            <span className="text-foreground-600 text-xs font-medium">{targetLabel}</span>
          </div>
        </div>

        {/* Manual button */}
        <a
          href={redirect.customDomain ? (redirect.customDomain.startsWith('http') ? redirect.customDomain : `https://${redirect.customDomain}`) : `/radio/${redirect.frequency}`}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 font-semibold rounded-xl transition-all cursor-pointer text-sm whitespace-nowrap"
        >
          <i className="ri-external-link-line"></i>
          Ir ahora
        </a>

        <p className="text-foreground-300 text-[10px] mt-4">
          Este enlace es permanente y nunca cambia
        </p>
      </div>
    </div>
  );
}